export const SOWING_STATUSES = [
  'matches_prescription',
  'differs',
  'unknown',
]

export const HARVEST_STATUSES = ['matches_sown', 'differs', 'unknown']

export const YIELD_MAP_COVERAGE_STATUSES = [
  'covers_harvest',
  'partial',
  'unknown',
]

export const HARVEST_SURFACE_TOLERANCE_HA = 0.01

export const emptySurfaceReconciliation = () => ({
  sowingStatus: '',
  declaredSownSurfaceHa: '',
  harvestStatus: '',
  declaredHarvestedSurfaceHa: '',
  yieldMapCoverageStatus: '',
})

const parseSurfaceHa = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const raw = String(value).trim()
  const normalized = raw.includes(',')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw
  const parsed = Number(normalized)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const parseReferenceSurfaceHa = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const raw = String(value).trim()
  const normalized = raw.includes(',')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw
  const parsed = Number(normalized)

  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

const getValue = (object, ...keys) => {
  if (!object) {
    return undefined
  }

  return keys.reduce((value, key) => {
    if (value !== undefined && value !== null) {
      return value
    }

    return object[key]
  }, undefined)
}

export const getPrescriptionSurfaceReference = (inspection) => {
  const landIntersection =
    getValue(inspection, 'landIntersection', 'land_intersection') || {}
  const intersectionSurfaceHa = parseReferenceSurfaceHa(
    getValue(
      landIntersection,
      'intersectionSurfaceHa',
      'intersection_surface_ha'
    )
  )
  const outsideSurfaceHa = parseReferenceSurfaceHa(
    getValue(landIntersection, 'outsideSurfaceHa', 'outside_surface_ha')
  )

  if (intersectionSurfaceHa !== null) {
    return {
      kind: 'inside_land',
      outsideSurfaceHa,
      surfaceHa: intersectionSurfaceHa,
    }
  }

  const uploadedSurfaceHa = parseReferenceSurfaceHa(
    getValue(inspection, 'surfaceHa', 'surface_ha')
  )

  if (uploadedSurfaceHa !== null) {
    return {
      kind: 'uploaded_pending_clip',
      outsideSurfaceHa,
      surfaceHa: uploadedSurfaceHa,
    }
  }

  return {
    kind: 'unavailable',
    outsideSurfaceHa,
    surfaceHa: null,
  }
}

export const getSurfaceReconciliationAssessment = (
  reconciliation,
  trustedPrescriptionSurfaceHa = null
) => {
  const value = reconciliation || emptySurfaceReconciliation()
  const errors = {}
  const hasSowingStatus = SOWING_STATUSES.includes(value.sowingStatus)
  const declaredSownSurfaceHa =
    value.sowingStatus === 'differs'
      ? parseSurfaceHa(value.declaredSownSurfaceHa)
      : null

  if (!hasSowingStatus) {
    errors.sowingStatus = 'required'
  } else if (
    value.sowingStatus === 'differs' &&
    declaredSownSurfaceHa === null
  ) {
    errors.declaredSownSurfaceHa = 'positive_surface'
  }

  const sowingValid =
    hasSowingStatus &&
    (value.sowingStatus !== 'differs' || declaredSownSurfaceHa !== null)
  const parsedTrustedPrescriptionSurfaceHa = parseSurfaceHa(
    trustedPrescriptionSurfaceHa
  )
  const resolvedSownSurfaceHa =
    value.sowingStatus === 'differs'
      ? declaredSownSurfaceHa
      : value.sowingStatus === 'matches_prescription'
      ? parsedTrustedPrescriptionSurfaceHa
      : null

  const hasHarvestStatus = HARVEST_STATUSES.includes(value.harvestStatus)
  const declaredHarvestedSurfaceHa =
    value.harvestStatus === 'differs'
      ? parseSurfaceHa(value.declaredHarvestedSurfaceHa)
      : null

  if (sowingValid && !hasHarvestStatus) {
    errors.harvestStatus = 'required'
  } else if (
    sowingValid &&
    value.harvestStatus === 'differs' &&
    declaredHarvestedSurfaceHa === null
  ) {
    errors.declaredHarvestedSurfaceHa = 'positive_surface'
  } else if (
    sowingValid &&
    value.harvestStatus === 'differs' &&
    resolvedSownSurfaceHa !== null &&
    declaredHarvestedSurfaceHa >
      resolvedSownSurfaceHa + HARVEST_SURFACE_TOLERANCE_HA
  ) {
    errors.declaredHarvestedSurfaceHa = 'harvest_exceeds_sown'
  }

  const harvestValid =
    sowingValid &&
    hasHarvestStatus &&
    (value.harvestStatus !== 'differs' ||
      (declaredHarvestedSurfaceHa !== null &&
        !errors.declaredHarvestedSurfaceHa))
  const resolvedHarvestedSurfaceHa =
    value.harvestStatus === 'differs'
      ? declaredHarvestedSurfaceHa
      : value.harvestStatus === 'matches_sown'
      ? resolvedSownSurfaceHa
      : null
  const hasYieldMapCoverageStatus = YIELD_MAP_COVERAGE_STATUSES.includes(
    value.yieldMapCoverageStatus
  )

  if (harvestValid && !hasYieldMapCoverageStatus) {
    errors.yieldMapCoverageStatus = 'required'
  }

  const coverageValid = harvestValid && hasYieldMapCoverageStatus
  const valid = sowingValid && harvestValid && coverageValid

  return {
    coverageValid,
    declaredHarvestedSurfaceHa,
    declaredSownSurfaceHa,
    errors,
    harvestValid,
    payload: valid
      ? {
          sowingStatus: value.sowingStatus,
          declaredSownSurfaceHa:
            value.sowingStatus === 'differs' ? declaredSownSurfaceHa : null,
          harvestStatus: value.harvestStatus,
          declaredHarvestedSurfaceHa:
            value.harvestStatus === 'differs'
              ? declaredHarvestedSurfaceHa
              : null,
          yieldMapCoverageStatus: value.yieldMapCoverageStatus,
        }
      : null,
    resolvedHarvestedSurfaceHa,
    resolvedSownSurfaceHa,
    sowingValid,
    valid,
  }
}

export const changeSowingStatus = (current, sowingStatus) => {
  if (current?.sowingStatus === sowingStatus) {
    return current
  }

  return {
    ...emptySurfaceReconciliation(),
    sowingStatus: SOWING_STATUSES.includes(sowingStatus) ? sowingStatus : '',
  }
}

export const changeDeclaredSownSurfaceHa = (
  current,
  declaredSownSurfaceHa
) => ({
  ...emptySurfaceReconciliation(),
  sowingStatus: current?.sowingStatus || '',
  declaredSownSurfaceHa,
})

export const changeHarvestStatus = (current, harvestStatus) => {
  if (current?.harvestStatus === harvestStatus) {
    return current
  }

  return {
    ...current,
    harvestStatus: HARVEST_STATUSES.includes(harvestStatus)
      ? harvestStatus
      : '',
    declaredHarvestedSurfaceHa: '',
    yieldMapCoverageStatus: '',
  }
}

export const changeDeclaredHarvestedSurfaceHa = (
  current,
  declaredHarvestedSurfaceHa
) => ({
  ...current,
  declaredHarvestedSurfaceHa,
  yieldMapCoverageStatus: '',
})

export const changeYieldMapCoverageStatus = (
  current,
  yieldMapCoverageStatus
) => ({
  ...current,
  yieldMapCoverageStatus: YIELD_MAP_COVERAGE_STATUSES.includes(
    yieldMapCoverageStatus
  )
    ? yieldMapCoverageStatus
    : '',
})

export const clearYieldMapCoverageStatus = (current) => ({
  ...current,
  yieldMapCoverageStatus: '',
})
